import { makeStyles } from '@material-ui/styles'
import ApiClient from 'api-client/ApiClient'
import ServiceUnhealthyInfo from 'app/plugins/openstack/components/common/ServiceUnhealthyInfo'
import { listFlavors } from 'app/plugins/openstack/components/flavors/actions'
import { flavorsSelector } from 'app/plugins/openstack/components/flavors/selectors'
import { waitSeconds } from 'app/plugins/openstack/components/infrastructure/blueprint/helpers'
import { listNetworks } from 'app/plugins/openstack/components/networks/actions'
import { networksSelector } from 'app/plugins/openstack/components/networks/selectors'
import { listStoragePools } from 'app/plugins/openstack/components/storage/pools/actions'
import { storagePoolsSelector } from 'app/plugins/openstack/components/storage/pools/selectors'
import { RootState, useAppSelector } from 'app/store'
import { ProgressStatus, StepProgress } from 'core/components/progress/StepProgress'
import useListAction from 'core/hooks/useListAction'
import useParams from 'core/hooks/useParams'
import useUpdateAction from 'core/hooks/useUpdateAction'
import { SessionState, sessionStoreKey } from 'core/session/sessionReducers'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isAirgapped } from 'core/utils/helpers'
import AddNodeGroups from 'k8s/components/capacity-and-health/AddNodeGroups'
import {
  NodePoolErrors,
  UNSUPPORTED_VM_FLAVORS,
} from 'k8s/components/cluster/deployment/form-components/ServerConfigurationFields'
import { listKaapiClusters, updateNodePools } from 'k8s/components/kaapi/clusters/actions'
import { kaapiClustersOverviewSelector } from 'k8s/components/kaapi/clusters/selectors'
import { createKaapiKubeadmConfigTemplate } from 'k8s/components/kaapi/kubeadm-config-templates/actions'
import { createKaapiMachineDeploymentBody } from 'k8s/components/kaapi/machine-deployment/action-helpers'
import {
  createKaapiMachineDeployment,
  listKaapiMachineDeployments,
} from 'k8s/components/kaapi/machine-deployment/actions'
import { createKaapiOpenStackMachineTemplateBody } from 'k8s/components/kaapi/openstack-machine-templates/action-helpers'
import { createKaapiOpenStackMachineTemplate } from 'k8s/components/kaapi/openstack-machine-templates/actions'
import DataKeys from 'k8s/DataKeys'
import ModalForm from 'pf9-ui-components/built/elements/modal/ModalForm'
import Theme from 'pf9-ui-components/built/theme-manager/themes/model'
import { prop } from 'ramda'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import useReactRouter from 'use-react-router'
import { isNilOrEmpty } from 'utils/fp'
import { v4 as uuidv4 } from 'uuid'
import { ProgressStep, useClusterProgress } from '../access-control/hooks/useClusterProgress'
import { DEFAULT_VOLUME_SIZE, getDUInternalIP } from '../cluster/deployment/form-components/helpers'
import { createNodeGroupTemplateBody } from '../kaapi/clusters/action-helpers'
import { listKaapiConfigMaps } from '../kaapi/config-maps/actions'
import { kaapiConfigMapsSelector } from '../kaapi/config-maps/selectors'
import { kaapiMachineDeploymentsForClusterSelector } from '../kaapi/machine-deployment/selectors'
import { sshKeysSelector } from 'app/plugins/openstack/components/security/keys/selectors'

const { kubernetes } = ApiClient.getInstance()

const validateNodePools = (nodePools) => {
  const errors = {}
  nodePools.forEach(({ id, flavor, replicas }) => {
    if (!flavor || !replicas) {
      errors[id] = errors[id] || {}
      if (!flavor) {
        errors[id]['flavor'] = 'A VM flavor selection is required.'
      }
      if (!replicas) {
        errors[id]['replicas'] = 'The value must be greater or equal than 1'
      }
    }
  })
  return errors
}

const initialValues = {
  nodePools: [{ id: 1 }],
  openstackCluster: '',
  imageName: '',
  k8sVersion: '',
  sourceType: 'image',
  volumeSize: DEFAULT_VOLUME_SIZE,
  volumeType: '',
  sshKey: null,
}

const AddNodeGroupsModal = (props) => {
  const classes = useStyles()
  const { history } = useReactRouter()
  const { params, getParamsUpdater } = useParams<Record<string, any>>(initialValues)
  const [showProgress, setShowProgress] = useState(false)
  const [isProgressComplete, setIsProgressComplete] = useState(false)
  const { progressDetails, updateProgress, resetProgress } = useClusterProgress({
    initLabel: 'Updating Cluster',
  })

  const { updatePrefs: updateK8sPluginGlobalParams } = useScopedPreferences('k8sPluginGlobalParams')
  const { activeKaapiTenant } = useSelector<RootState, SessionState>(prop(sessionStoreKey))
  const namespace = activeKaapiTenant
  const [nodePoolErrors, setNodePoolErrors] = useState<NodePoolErrors>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  // Get cluster name from global preferences
  const { prefs: k8sPluginGlobalPerfs } = useScopedPreferences('k8sPluginGlobalParams')
  const clusterName: string = useMemo(() => k8sPluginGlobalPerfs?.cluster as string, [
    k8sPluginGlobalPerfs.cluster,
  ])
  const {
    prefs: { currentTenant },
  } = useScopedPreferences()

  const isAirgappedEnv = isAirgapped()

  const { loading: loadingKaapiClusters } = useListAction(listKaapiClusters)
  const clusters = useSelector(kaapiClustersOverviewSelector)

  const clusterDetails = useMemo(() => {
    return clusters?.find((cluster) => cluster?.metadata?.name === clusterName)
  }, [clusters, clusterName])

  // Fetch existing Machine Deployments
  const { reload: reloadMachineDeployments } = useListAction(listKaapiMachineDeployments)
  const machineDeploymentSelector = kaapiMachineDeploymentsForClusterSelector({
    clusterName: k8sPluginGlobalPerfs.cluster,
  })
  const initialMachineDeployments = useSelector(machineDeploymentSelector)

  // VM Flavors
  const { loading: loadingVmFlavors } = useListAction(listFlavors, {
    params,
  })
  // Filter out unsupported VM Flavors
  const vmFlavors = useAppSelector(flavorsSelector)

  const filteredFlavors = useMemo(() => {
    return vmFlavors?.filter(
      (vm) => !UNSUPPORTED_VM_FLAVORS.includes(vm?.name) && vm?.vcpus > 0 && vm?.ram > 0, // Filter out unsupported VM Flavors && hotplug flavors
    )
  }, [vmFlavors])

  // Load ssh keys
  const sshKeys = useSelector(sshKeysSelector)

  // Storage Pools
  const { loading: loadingStoragePools } = useListAction(listStoragePools)
  const storagePools = useAppSelector(storagePoolsSelector)

  // Networks
  const { loading: loadingNetworks } = useListAction(listNetworks)
  const allNetworks = useAppSelector(networksSelector)
  // Filter networks by tenant
  const networksInTenant = useMemo(() => {
    return allNetworks?.filter((network) => {
      return network?.project_id === currentTenant || network?.shared
    })
  }, [allNetworks, currentTenant])

  // Load Config Maps
  const { loading: loadingConfigMaps } = useListAction(listKaapiConfigMaps, {
    params: {
      namespace,
    },
    requiredParams: ['namespace'],
  })
  const configMaps = useSelector(kaapiConfigMapsSelector)
  const duInternalIP = getDUInternalIP(configMaps)

  // Create Actions
  const {
    update: createKubeadmConfigTemplate,
    updating: creatingKubeadmConfigTemplate,
    error: createKubeadmConfigTemplateError,
  } = useUpdateAction(createKaapiKubeadmConfigTemplate)
  const {
    update: createOpenStackMachineTemplate,
    updating: creatingOpenStackMachineTemplate,
    error: createOpenStackMachineTemplateError,
  } = useUpdateAction(createKaapiOpenStackMachineTemplate)
  const {
    update: createNodePools,
    updating: creatingNodePools,
    error: createNodePoolsError,
  } = useUpdateAction(updateNodePools)
  const {
    update: createMachineDeployment,
    updating: creatingMachineDeployment,
    error: createMachineDeploymentError,
  } = useUpdateAction(createKaapiMachineDeployment)

  const hasError =
    !!createKubeadmConfigTemplateError ||
    !!createOpenStackMachineTemplateError ||
    !!createMachineDeploymentError ||
    !!error ||
    !!createNodePoolsError

  useEffect(() => {
    if (!hasError) return
    // Show first error
    const errorObj =
      createKubeadmConfigTemplateError ||
      createOpenStackMachineTemplateError ||
      createMachineDeploymentError ||
      error ||
      createNodePoolsError

    setError({
      title: errorObj?.title || 'Error creating cluster',
      message:
        errorObj?.message || 'An error occurred while creating the cluster. Please try again.',
    })
  }, [
    hasError,
    createKubeadmConfigTemplateError,
    createOpenStackMachineTemplateError,
    createMachineDeploymentError,
    error,
    createNodePoolsError,
  ])

  const handleSubmit = async () => {
    setError(null)
    setLoading(true)
    setShowProgress(!!clusterDetails?.spec?.topology)

    if (isAirgappedEnv && !duInternalIP) {
      setLoading(false)
      setError({
        title: 'Error',
        message: 'Something went wrong, please try again. Tenant may not be configured correctly',
      })
      updateProgress(ProgressStep.Init, ProgressStatus.Failed)
      return
    }

    const nodePoolErrors = validateNodePools(params.nodePools)
    setNodePoolErrors(nodePoolErrors)
    if (
      !isNilOrEmpty(nodePoolErrors) ||
      !params.imageName ||
      !params.openstackCluster ||
      !params?.sshKey
    ) {
      setLoading(false)
      setShowProgress(false)
      setError({
        title: 'Error',
        message: 'Please fill all the required fields.',
      })
      updateProgress(ProgressStep.Init, ProgressStatus.Failed)
      return
    }

    // Generate a short UUID
    const shortUUID = uuidv4().slice(0, 8)
    const name = `md-${shortUUID}`

    if (clusterDetails?.spec?.topology) {
      const nodePoolsBody = createNodeGroupTemplateBody({
        openstackCluster: params.openstackCluster,
        nodePool: params?.nodePools[0],
        volumeType: params?.volumeType,
        volumeSize: params?.volumeSize,
        sourceType: params?.sourceType,
        image: params?.imageName,
        name,
        sshKey: sshKeys.find((key) => key.name === params?.sshKey),
      })
      updateProgress(ProgressStep.Init, ProgressStatus.Success)

      try {
        const { response: updatedNodeGroups, success } = await createNodePools({
          namespace,
          body: nodePoolsBody,
          clusterName,
        })

        if (!hasError && success) {
          // Reload the machine deployments and machines
          updateProgress(ProgressStep.ControlPlane, ProgressStatus.Success)
          let isNodeGroupAdded = false
          const maxAttempts = 60 // 5 minutes timeout (60 * 5 seconds)
          let attempts = 0

          while (!isNodeGroupAdded && attempts < maxAttempts) {
            attempts++
            const machineDeployments = await kubernetes.getAllCustomResources(
              DataKeys.KaapiMachineDeployments,
            )
            const clusterMachines = machineDeployments?.filter(
              (md) => md?.spec?.clusterName === clusterName,
            )
            const newMachine =
              clusterMachines?.find(
                (md) =>
                  !initialMachineDeployments.some(
                    (md2) => md2?.metadata?.name === md?.metadata?.name,
                  ),
              ) || {}
            if (
              clusterMachines?.length ===
                updatedNodeGroups?.spec?.topology?.workers?.machineDeployments?.length &&
              !!newMachine?.status?.phase
            ) {
              isNodeGroupAdded = true
              updateProgress(ProgressStep.NodeGroups, ProgressStatus.Success)
              reloadMachineDeployments(true, false)
              setIsProgressComplete(true)
              setShowProgress(false)
              setLoading(false)
            } else {
              await waitSeconds(5)
            }
          }

          if (!isNodeGroupAdded) {
            throw new Error('Node groups are taking too long to get deployed')
          }
        }
      } catch (err) {
        setLoading(false)
        updateProgress(ProgressStep.ControlPlane, ProgressStatus.Failed)
        setError({
          title: 'Error',
          message: 'Something went wrong, please try again.',
        })
        console.error(err)
        return
      }
    } else {
      // No need to show progress screen for legacy clusters
      setLoading(false)
      setShowProgress(false)

      // Else part needs to be deleted after migration in later releases
      // For each nodepool, create KubeadmConfigTemplate, MachineDeployment and OpenStackMachineTemplate
      for (const [idx, nodePool] of params.nodePools.entries()) {
        // Create OpenStackMachineTemplate
        const openStackMachineTemplateBody = createKaapiOpenStackMachineTemplateBody({
          name,
          namespace,
          flavor: nodePool.flavor,
          clusterName,
          storage: nodePool.storage,
          network: nodePool.network,
          subnet: nodePool.subnet,
          imageName: params.imageName,
          allowedAddressesPools:
            clusterDetails?.metalLBAddon?.spec?.clusterSpec?.firewallConfig?.allowedAddressPools ||
            [],
          sizeGiB: Number(params.volumeSize),
          type: params.volumeType,
          sourceType: params.sourceType,
        })
        await createOpenStackMachineTemplate({ namespace, body: openStackMachineTemplateBody })

        // Create MachineDeployment
        const machineDeploymentCreateBody = createKaapiMachineDeploymentBody({
          name,
          namespace,
          clusterName,
          replicas: nodePool.replicas,
          configTemplateRefName: clusterName,
          infrastructureRefName: name,
          k8sVersion: props?.k8sVersion,
          autoscaling: nodePool.autoscaling,
          openstackCluster: params.openstackCluster,
        })
        await createMachineDeployment({ namespace, body: machineDeploymentCreateBody })
      }

      if (!hasError) {
        // Reload the machine deployments and machines
        reloadMachineDeployments(true, false)
        props.onClose()
      }
    }
  }

  const submitting =
    creatingKubeadmConfigTemplate ||
    creatingOpenStackMachineTemplate ||
    creatingMachineDeployment ||
    creatingNodePools ||
    loading

  const handleNodePoolChange = useCallback(
    (id) => (newValues) => {
      const updatedNodePools = params.nodePools.map((nodePool) => {
        if (nodePool.id === id) {
          return { ...nodePool, ...newValues }
        }
        return nodePool
      })
      getParamsUpdater('nodePools')(updatedNodePools)
    },
    [params.nodePools, getParamsUpdater],
  )

  const handleReset = () => {
    setShowProgress(false)
    setIsProgressComplete(false)
    resetProgress()
    if (!hasError) props.onClose()
  }

  const emptyDeleteHandler = useCallback(() => {}, [])

  return (
    <ModalForm
      {...props}
      open
      title="Add Node Group"
      maxWidth={820}
      className={classes.modal}
      onSubmit={showProgress || isProgressComplete ? handleReset : handleSubmit}
      submitTitle={showProgress || isProgressComplete ? 'Finish' : 'Add Node Group'}
      error={error}
      submitting={submitting}
      onClose={props.onClose}
      onBackButtonClick={handleReset}
      showBackButton={hasError && showProgress}
      data-testid="add-node-group-modal"
    >
      {!showProgress && !isProgressComplete ? (
        <>
          <ServiceUnhealthyInfo
            action="Node Group Creation"
            hypervisor
            imageLibrary
            blockStorage
            network
          />
          <AddNodeGroups
            nodePool={params.nodePools[0]}
            params={params}
            vmFlavors={filteredFlavors}
            storagePools={storagePools}
            networks={networksInTenant}
            showDeleteButton={false}
            onDeleteNodePool={emptyDeleteHandler}
            errors={nodePoolErrors[params.nodePools[0].id]}
            showOpenstackCluster
            getParamsUpdater={getParamsUpdater}
            onChange={handleNodePoolChange(params.nodePools[0].id)}
            k8sVersion={props?.k8sVersion}
            loadingVmFlavors={loadingVmFlavors}
          />
        </>
      ) : (
        <StepProgress
          progressDetails={progressDetails}
          title={`Node group is getting added. 
              Please wait and do not close this dialog, or the process will be cancelled.`}
        />
      )}
    </ModalForm>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  modal: {
    '& .modal-body': {
      padding: '32px 24px 24px 32px',
    },
    '& .modal-body .progress-root .progressContent > form': {
      marginBottom: 0,
    },
  },
}))

export default AddNodeGroupsModal
